import {
  buildMarketUrl,
  getFullMarketContext,
  getMarketBootstrapContext,
  getMarketFromLocation,
  getResolvedMarketContext,
  getTargetForCountry,
  isExplicitMarketUrl,
  saveMarketContext
} from '@theme/market-context';

const USER_WRAPPER_SELECTOR = '.user-dropdown-wrapper';
const USER_BUTTON_SELECTOR = '#mobile-header-user-btn, .mobile-header__user-btn, #header-user-btn, .header-user-btn';
const USER_DROPDOWN_SELECTOR = '.user-dropdown';
const COUNTRY_NAMES = {
  AE: 'UAE',
  SA: 'KSA',
  KW: 'KWT',
  QA: 'QAT',
  OM: 'OMN',
  BH: 'BHR',
  US: 'USA',
  AU: 'AUS',
  CA: 'CAN',
  AM: 'Armenia',
  FR: 'France',
  DE: 'Germany',
  NL: 'Netherlands',
  GB: 'United Kingdom',
  IT: 'Italy',
  ES: 'Spain',
  BE: 'Belgium',
  CH: 'Switzerland',
  SE: 'Sweden',
  NO: 'Norway',
  BG: 'Bulgaria',
  TR: 'Turkey',
  EU: 'International'
};
const ARABIC_SUPPORTED_COUNTRIES = ['AE', 'SA', 'QA', 'KW', 'OM', 'BH'];
const REGION_QUESTION_DISMISSED_KEY = 'amprio_region_question_dismissed';
const GEOIP_CACHE_KEY = 'amprio_geoip_context';
const GEOIP_CACHE_TTL = 24 * 60 * 60 * 1000;
const MARKET_SYNC_REDIRECT_KEY = 'amprio_market_sync_redirect_v1';
const GEO_COUNTRY_NAMES = {
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  QA: 'Qatar',
  KW: 'Kuwait',
  OM: 'Oman',
  BH: 'Bahrain',
  US: 'United States',
  AU: 'Australia',
  CA: 'Canada',
  EU: 'International',
  AM: 'Armenia',
  FR: 'France',
  DE: 'Germany',
  NL: 'Netherlands',
  GB: 'United Kingdom',
  IT: 'Italy',
  ES: 'Spain',
  BE: 'Belgium',
  CH: 'Switzerland',
  SE: 'Sweden',
  NO: 'Norway',
  BG: 'Bulgaria',
  TR: 'Turkey'
};

let userDropdownEventsBound = false;
let countrySelectorInitialized = false;
let regionQuestionInitialized = false;
let regionQuestionPending = null;
let marketNavigationInFlight = false;
let langSwitchInFlight = false;

function isShopifyDesignMode() {
  return document.documentElement.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);
}

function normalizeMarketUrl(url) {
  try {
    const parsed = new URL(String(url || '/'), window.location.origin);
    parsed.hash = '';
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    parsed.pathname = path;
    return parsed.hostname + parsed.pathname + parsed.search;
  } catch (_) {
    return String(url || '');
  }
}

function isSameDocumentUrl(targetUrl) {
  return normalizeMarketUrl(targetUrl) === normalizeMarketUrl(window.location.href);
}

function getCurrentPageLocale() {
  const bootstrapLocale = String(
    (window.theme && window.theme.marketBootstrap && window.theme.marketBootstrap.locale) ||
      window.theme?.locale ||
      document.documentElement.lang ||
      ''
  ).toLowerCase();
  if (bootstrapLocale.indexOf('ar') === 0) return 'ar';

  const fromLocation = getMarketFromLocation(window.location.pathname);
  return String(fromLocation.locale || '').toLowerCase() === 'ar' ? 'ar' : 'en';
}

function canPerformMarketSyncRedirect(nextUrl) {
  if (isSameDocumentUrl(nextUrl)) return false;

  try {
    const raw = sessionStorage.getItem(MARKET_SYNC_REDIRECT_KEY);
    if (!raw) return true;
    const entry = JSON.parse(raw);
    const age = Date.now() - Number(entry.at || 0);
    if (age > 15000) return true;

    const sameFrom = normalizeMarketUrl(entry.from) === normalizeMarketUrl(window.location.href);
    const sameTo = normalizeMarketUrl(entry.to) === normalizeMarketUrl(nextUrl);
    if (sameFrom && sameTo && Number(entry.count || 0) >= 1) return false;
    if (Number(entry.count || 0) >= 3 && age < 8000) return false;
  } catch (_) {}

  return true;
}

function markMarketSyncRedirect(nextUrl) {
  try {
    const previous = JSON.parse(sessionStorage.getItem(MARKET_SYNC_REDIRECT_KEY) || '{}');
    const sameHop =
      normalizeMarketUrl(previous.from) === normalizeMarketUrl(window.location.href) &&
      normalizeMarketUrl(previous.to) === normalizeMarketUrl(nextUrl);
    sessionStorage.setItem(
      MARKET_SYNC_REDIRECT_KEY,
      JSON.stringify({
        from: window.location.href,
        to: nextUrl,
        at: Date.now(),
        count: sameHop ? Number(previous.count || 0) + 1 : 1
      })
    );
  } catch (_) {}
}

function clearMarketSyncRedirectGuard() {
  try {
    sessionStorage.removeItem(MARKET_SYNC_REDIRECT_KEY);
  } catch (_) {}
}

function setMarketSwitchBusy(isBusy) {
  document.querySelectorAll('.lang-switch[data-lang-switch], [data-popup-trigger="delivery-location"]').forEach(function(el) {
    if (isBusy) {
      el.setAttribute('aria-busy', 'true');
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.removeAttribute('aria-busy');
      el.removeAttribute('aria-disabled');
    }
  });
}

function normalizeCountryCode(value) {
  return String(value || '').trim().toUpperCase();
}

function getCurrentContext() {
  return getResolvedMarketContext();
}

function getCurrentCountryName(code) {
  const normalized = normalizeCountryCode(code);
  return COUNTRY_NAMES[normalized] || window.theme?.countryNames?.[normalized] || normalized || 'AE';
}

function isRestrictedPath(pathname = window.location.pathname) {
  const path = String(pathname || '/').toLowerCase();
  return path.startsWith('/cart') || path.startsWith('/checkout') || path.startsWith('/account') || path.startsWith('/orders');
}

function supportsArabicLanguage(countryCode) {
  return ARABIC_SUPPORTED_COUNTRIES.includes(normalizeCountryCode(countryCode));
}

function getShopifyCountryCode() {
  return normalizeCountryCode(window.theme?.shopifyCountry || window.theme?.defaultCountry || 'AE');
}

function getAvailableShopifyCountryCodes() {
  const codes = window.theme?.availableCountryCodes;
  if (!Array.isArray(codes)) return [];
  return codes.map(normalizeCountryCode).filter(Boolean);
}

function isManualMarketContext(context) {
  const locationSource = String(context?.locationSource || '').toLowerCase();
  const source = String(context?.source || '').toLowerCase();
  return locationSource === 'manual' || source === 'country_confirm';
}

function submitShopifyLocalization(countryCode, locale) {
  if (isShopifyDesignMode()) return false;

  const code = normalizeCountryCode(countryCode);
  const availableCodes = getAvailableShopifyCountryCodes();
  if (!code || !availableCodes.includes(code)) return false;

  const form = document.getElementById('delivery-location-localization-form');
  const countryInput = document.getElementById('delivery-location-localization-country');
  const languageInput = document.getElementById('delivery-location-localization-language');
  if (!(form instanceof HTMLFormElement) || !countryInput) return false;

  countryInput.value = code;
  if (languageInput) languageInput.value = String(locale || 'en').toLowerCase() === 'ar' ? 'ar' : 'en';
  HTMLFormElement.prototype.submit.call(form);
  return true;
}

function readGeoIpCache() {
  try {
    const raw = localStorage.getItem(GEOIP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.updatedAt) return null;
    const age = Date.now() - new Date(parsed.updatedAt).getTime();
    if (!age || age > GEOIP_CACHE_TTL) return null;
    if (!parsed.countryCode) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function saveGeoIpCache(payload) {
  try {
    localStorage.setItem(GEOIP_CACHE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('market-navigation', error);
  }
}

async function detectLocationByIp() {
  const cached = readGeoIpCache();
  if (cached && cached.countryCode) {
    return cached;
  }

  const controller = new AbortController();
  const timer = setTimeout(function() {
    controller.abort();
  }, 5000);

  try {
    const response = await fetch('https://ipwho.is/?fields=success,country,country_code,city', {
      signal: controller.signal,
      credentials: 'omit'
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || data.success === false) return null;
    const countryCode = normalizeCountryCode(data.country_code);
    if (!countryCode) return null;
    const result = {
      countryCode: countryCode,
      countryName: data.country || GEO_COUNTRY_NAMES[countryCode] || countryCode,
      city: data.city || '',
      locationSource: 'auto',
      updatedAt: new Date().toISOString()
    };
    saveGeoIpCache(result);
    return result;
  } catch (_) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function formatGeoLocationLabel(detected) {
  const countryCode = normalizeCountryCode(detected.countryCode);
  const countryName = GEO_COUNTRY_NAMES[countryCode] || detected.countryName || countryCode;
  const city = String(detected.city || '').trim();
  if (city) return city + ', ' + countryName;
  return countryName;
}

function shouldOfferRegionQuestion(detected) {
  if (isRestrictedPath()) return false;
  try {
    if (sessionStorage.getItem(REGION_QUESTION_DISMISSED_KEY)) return false;
  } catch (_) {}

  if (detected && detected.countryCode) {
    const current = getMarketFromLocation(window.location.pathname);
    const currentCode = normalizeCountryCode(current.countryCode);
    const detectedCode = normalizeCountryCode(detected.countryCode);
    const currentTarget = getTargetForCountry(currentCode);
    const detectedTarget = getTargetForCountry(detectedCode);
    if (!isSameTarget(currentTarget, detectedTarget)) return true;
    if (currentCode !== detectedCode) return true;
    if (String(detected.city || '').trim()) return true;
  }

  const stored = getFullMarketContext() || {};
  if (stored.locationSource === 'geo_confirm') return false;
  if (stored.locationSource === 'manual') return false;
  if (stored.source === 'country_confirm') return false;
  if (stored.explicit && stored.locationSource !== 'auto') return false;
  if (isExplicitMarketUrl(window.location.pathname)) return false;
  return Boolean(detected && detected.countryCode);
}

function dismissRegionQuestion() {
  const popup = document.getElementById('region-question-popup');
  if (popup) {
    popup.classList.remove('is-visible');
    popup.hidden = true;
  }
  regionQuestionPending = null;
  try {
    sessionStorage.setItem(REGION_QUESTION_DISMISSED_KEY, '1');
  } catch (_) {}
}

function measureRegionQuestionTop(popup) {
  const isMobile = window.innerWidth <= 1024;
  const selectors = isMobile
    ? [
        '.theme-mobile-nav--sticky',
        '.block_navigation_header_mob--sticky',
        '#header-group .theme-mobile-nav'
      ]
    : [
        '.block_navigation_menu--sticky',
        '#header-group .theme-header',
        '#header-group .block_navigation_header',
        '#header-group .block_navigation_menu'
      ];
  let headerBottom = 0;

  document.querySelectorAll(selectors.join(',')).forEach(function(element) {
    if (!(element instanceof HTMLElement)) return;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return;

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
    headerBottom = Math.max(headerBottom, Math.min(rect.bottom, window.innerHeight));
  });

  if (headerBottom <= 0) {
    const variableName = isMobile ? '--mobile-sticky-header-height' : '--header-group-height';
    const variableTarget = isMobile ? document.documentElement : document.body;
    const fallbackHeight = parseFloat(getComputedStyle(variableTarget).getPropertyValue(variableName));
    if (Number.isFinite(fallbackHeight) && fallbackHeight > 0) headerBottom = fallbackHeight;
  }

  if (isMobile) {
    const mobileStackHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--mobile-sticky-header-height')
    );
    if (Number.isFinite(mobileStackHeight) && mobileStackHeight > 0) {
      headerBottom = Math.max(headerBottom, mobileStackHeight);
    }
  }

  const popupStyles = getComputedStyle(popup);
  const gapVariable = isMobile
    ? '--region-question-mobile-gap'
    : document.querySelector('.block_navigation_menu--sticky')
      ? '--region-question-desktop-sticky-gap'
      : '--region-question-desktop-gap';
  const measuredGap = parseFloat(popupStyles.getPropertyValue(gapVariable));
  const gap = Number.isFinite(measuredGap) ? measuredGap : 10;
  return Math.round(Math.max(0, headerBottom) + gap);
}

function syncRegionQuestionTop(popup) {
  if (!popup) return;
  popup.style.setProperty('--region-question-top', measureRegionQuestionTop(popup) + 'px');
}

function showRegionQuestion(detected) {
  const popup = document.getElementById('region-question-popup');
  const label = popup ? popup.querySelector('[data-region-question-label]') : null;
  if (!popup || !label) return;
  regionQuestionPending = detected;
  label.textContent = formatGeoLocationLabel(detected);
  popup.hidden = false;
  syncRegionQuestionTop(popup);
  requestAnimationFrame(function() {
    syncRegionQuestionTop(popup);
    requestAnimationFrame(function() {
      syncRegionQuestionTop(popup);
      popup.classList.add('is-visible');
    });
  });
}

function dispatchDeliveryLocationUpdated(detail) {
  document.dispatchEvent(
    new CustomEvent('theme:delivery-location-updated', {
      bubbles: true,
      detail: detail || {}
    })
  );
}

function confirmRegionQuestion() {
  const detected = regionQuestionPending;
  if (!detected) return;

  const countryCode = normalizeCountryCode(detected.countryCode);
  const target = getTargetForCountry(countryCode);
  const current = getMarketFromLocation(window.location.pathname);
  const currentCode = normalizeCountryCode(current.countryCode);
  const locale = supportsArabicLanguage(countryCode) && String(current.locale || '').toLowerCase() === 'ar' ? 'ar' : 'en';

  saveCurrentMarketContext({
    ...getFullMarketContext(),
    ...target,
    countryCode: countryCode,
    country: countryCode.toLowerCase(),
    city: detected.city || '',
    countryName: GEO_COUNTRY_NAMES[countryCode] || detected.countryName || countryCode,
    locale: locale,
    locationSource: 'geo_confirm',
    explicit: true,
    source: 'geo_confirm'
  });

  dismissRegionQuestion();
  syncCountryDisplay();
  dispatchDeliveryLocationUpdated({
    countryCode: countryCode,
    countryName: GEO_COUNTRY_NAMES[countryCode] || detected.countryName,
    city: detected.city || ''
  });

  const storedTarget = getTargetForCountry(currentCode);
  const needsNavigation = currentCode !== countryCode || !isSameTarget(storedTarget, target);
  if (!needsNavigation) {
    clearMarketSyncRedirectGuard();
    return;
  }

  const nextUrl = buildMarketUrl(target, {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    preservePath: true,
    locale: locale
  });

  if (isSameDocumentUrl(nextUrl) || !canPerformMarketSyncRedirect(nextUrl)) {
    clearMarketSyncRedirectGuard();
    return;
  }

  navigateToMarketUrl(nextUrl);
}

function initRegionQuestion() {
  if (regionQuestionInitialized) return;
  const popup = document.getElementById('region-question-popup');
  if (!popup) return;
  regionQuestionInitialized = true;

  const confirmBtn = popup.querySelector('[data-region-question-confirm]');
  const changeBtn = popup.querySelector('[data-region-question-change]');
  const syncRegionQuestionButtonLabels = function() {
    const isMobile = window.innerWidth <= 749;
    [confirmBtn, changeBtn].forEach(function(btn) {
      if (!btn) return;
      const nextLabel = isMobile ? btn.dataset.labelMobile : btn.dataset.labelDesktop;
      if (nextLabel) btn.textContent = nextLabel;
    });
  };

  syncRegionQuestionButtonLabels();
  let regionQuestionPositionFrame = 0;
  const requestRegionQuestionPositionSync = function() {
    if (!popup.classList.contains('is-visible') || regionQuestionPositionFrame) return;
    regionQuestionPositionFrame = requestAnimationFrame(function() {
      regionQuestionPositionFrame = 0;
      syncRegionQuestionTop(popup);
    });
  };
  window.addEventListener('resize', function() {
    syncRegionQuestionButtonLabels();
    requestRegionQuestionPositionSync();
  }, { passive: true });
  window.addEventListener('scroll', requestRegionQuestionPositionSync, { passive: true });
  document.addEventListener('theme:desktop-sticky-change', requestRegionQuestionPositionSync);
  document.addEventListener('theme:mobile-sticky-change', requestRegionQuestionPositionSync);

  if (confirmBtn) {
    confirmBtn.addEventListener('click', function(e) {
      e.preventDefault();
      confirmRegionQuestion();
    });
  }

  if (changeBtn) {
    changeBtn.addEventListener('click', function(e) {
      e.preventDefault();
      dismissRegionQuestion();
      openDeliveryLocationPopup();
    });
  }

  const dismissBtn = popup.querySelector('[data-region-question-dismiss]');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', function(e) {
      e.preventDefault();
      dismissRegionQuestion();
    });
  }

  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Escape') return;
    if (!popup.classList.contains('is-visible')) return;
    dismissRegionQuestion();
  });
}

async function runRegionQuestionFlow() {
  const stored = getFullMarketContext() || {};
  if (isManualMarketContext(stored)) return;

  const detected = await detectLocationByIp();
  const detectedCode = normalizeCountryCode(detected?.countryCode);
  const availableCodes = getAvailableShopifyCountryCodes();
  if (!detectedCode || !availableCodes.includes(detectedCode)) return;

  const shopifyCountry = getShopifyCountryCode();
  const defaultCountry = normalizeCountryCode(window.theme?.defaultCountry || 'AE');
  if (shopifyCountry && shopifyCountry !== defaultCountry && shopifyCountry !== detectedCode) return;

  const target = getTargetForCountry(detectedCode);
  const detectedLocale = supportsArabicLanguage(detectedCode) ? getCurrentPageLocale() : 'en';
  saveCurrentMarketContext({
    ...stored,
    ...target,
    countryCode: detectedCode,
    country: detectedCode.toLowerCase(),
    city: detected.city || '',
    countryName: detected.countryName || GEO_COUNTRY_NAMES[detectedCode] || detectedCode,
    locale: detectedLocale,
    locationSource: 'auto',
    explicit: false,
    source: 'shopify_geo'
  });

  if (shopifyCountry === detectedCode) return;

  marketNavigationInFlight = true;
  setMarketSwitchBusy(true);
  if (!submitShopifyLocalization(detectedCode, detectedLocale)) {
    marketNavigationInFlight = false;
    setMarketSwitchBusy(false);
  }
}

function isSameTarget(a, b) {
  return String(a?.targetPrefix || '') === String(b?.targetPrefix || '');
}

function syncCountryDisplay() {
  const context = getCurrentContext();
  const label = getCurrentCountryName(getShopifyCountryCode() || context.countryCode || context.storeRegion);
  document.querySelectorAll('[data-country-display]').forEach(function(el) {
    el.textContent = label;
  });
}

function saveCurrentMarketContext(context, extra = {}) {
  const resolved = getTargetForCountry(context.countryCode || context.storeRegion || context.country || 'AE');
  return saveMarketContext({
    ...context,
    ...extra,
    countryCode: normalizeCountryCode(context.countryCode || context.storeRegion || context.country),
    country: normalizeCountryCode(context.countryCode || context.storeRegion || context.country).toLowerCase(),
    storeZone: context.storeZone || resolved.storeZone,
    storeRegion: context.storeRegion || resolved.storeRegion,
    targetPrefix: context.targetPrefix != null ? context.targetPrefix : resolved.targetPrefix,
    currency: context.currency || resolved.currency,
    locale: context.locale || 'en',
    updatedAt: new Date().toISOString()
  });
}

function navigateToMarketUrl(url) {
  const targetUrl = withCleanQueryAndHash(url);
  if (isSameDocumentUrl(targetUrl)) {
    clearMarketSyncRedirectGuard();
    return Promise.resolve();
  }
  if (marketNavigationInFlight) return Promise.resolve();
  marketNavigationInFlight = true;
  setMarketSwitchBusy(true);
  markMarketSyncRedirect(targetUrl);
  window.location.assign(targetUrl);
  return Promise.resolve();
}

function withCleanQueryAndHash(url) {
  const cleanSearch = sanitizeSearch(window.location.search);
  const hash = window.location.hash || '';
  try {
    const absolute = new URL(url, window.location.origin);
    absolute.search = cleanSearch;
    absolute.hash = hash;
    if (/^https?:\/\//i.test(String(url))) return absolute.toString();
    return absolute.pathname + absolute.search + absolute.hash;
  } catch (_) {
    return String(url || '/') + cleanSearch + hash;
  }
}

function sanitizeSearch(search) {
  const params = new URLSearchParams(search || '');
  params.delete('pb');
  const next = params.toString();
  return next ? '?' + next : '';
}

function stripPreviewBuilderParam() {
  try {
    if (!window.history || !window.history.replaceState) return;
    const current = new URL(window.location.href);
    if (!current.searchParams.has('pb')) return;
    current.searchParams.delete('pb');
    const nextSearch = current.searchParams.toString();
    const nextUrl = current.pathname + (nextSearch ? '?' + nextSearch : '') + current.hash;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch (error) {
    console.warn('market-navigation', error);
  }
}

function syncStoredMarketWithCurrentUrl() {
  const current = getMarketFromLocation(window.location.pathname);
  const bootstrap = getMarketBootstrapContext();
  const stored = getFullMarketContext() || {};
  const storedCode = normalizeCountryCode(stored.countryCode || stored.country || stored.storeRegion);
  const currentCode = getShopifyCountryCode() || normalizeCountryCode(current.countryCode);
  const directUrl = isExplicitMarketUrl(window.location.pathname);
  const preserveManualSelection = isManualMarketContext(stored) && storedCode === currentCode;

  if (directUrl) {
    const currentTarget = getTargetForCountry(currentCode);
    const storedTarget = storedCode ? getTargetForCountry(storedCode) : null;
    const preserveStoredCountry = currentCode === 'EU' && storedCode && storedTarget && isSameTarget(storedTarget, currentTarget);
    const nextCode = preserveStoredCountry ? storedCode : currentCode;
    const nextTarget = getTargetForCountry(nextCode);
    saveCurrentMarketContext({
      ...stored,
      ...bootstrap,
      ...current,
      ...nextTarget,
      countryCode: nextCode,
      country: nextCode.toLowerCase(),
      storeRegion: nextTarget.storeRegion,
      explicit: preserveStoredCountry ? Boolean(stored.explicit) : true,
      source: preserveStoredCountry ? (stored.source || 'stored') : 'url'
    });
    clearMarketSyncRedirectGuard();
    return;
  }

  saveCurrentMarketContext({
    ...stored,
    ...bootstrap,
    ...current,
    countryCode: currentCode,
    country: currentCode.toLowerCase(),
    locale: getCurrentPageLocale(),
    locationSource: preserveManualSelection ? 'manual' : (current.locationSource || 'auto'),
    explicit: preserveManualSelection,
    source: preserveManualSelection ? 'country_confirm' : (bootstrap.source || current.source || 'bootstrap')
  });
  clearMarketSyncRedirectGuard();
}

function openDeliveryLocationPopup() {
  if (typeof window.openDeliveryLocationPopup === 'function') {
    window.openDeliveryLocationPopup();
  }
}

function switchLanguage(nextLocale) {
  if (langSwitchInFlight || marketNavigationInFlight) return;

  const locale = String(nextLocale || 'en').toLowerCase() === 'ar' ? 'ar' : 'en';
  const current = getCurrentContext();
  const countryCode = normalizeCountryCode(current.countryCode || current.storeRegion || 'AE');
  const target = getTargetForCountry(countryCode);
  const resolvedLocale = supportsArabicLanguage(countryCode) ? locale : 'en';
  const currentLocale = getCurrentPageLocale();

  if (currentLocale === resolvedLocale) return;

  const nextUrl = buildMarketUrl(target, {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    preservePath: true,
    locale: resolvedLocale
  });

  if (isSameDocumentUrl(nextUrl)) {
    saveCurrentMarketContext({
      ...current,
      ...target,
      countryCode: countryCode,
      country: countryCode.toLowerCase(),
      locale: resolvedLocale,
      explicit: true,
      source: 'lang_switch'
    });
    return;
  }

  langSwitchInFlight = true;
  setMarketSwitchBusy(true);

  saveCurrentMarketContext({
    ...current,
    ...target,
    countryCode: countryCode,
    country: countryCode.toLowerCase(),
    locale: resolvedLocale,
    explicit: true,
    source: 'lang_switch'
  });

  if (typeof window.closeMobileMenuDrawer === 'function') {
    window.closeMobileMenuDrawer();
  }

  navigateToMarketUrl(nextUrl);
}

function initLangSwitch() {
  document.querySelectorAll('.lang-switch[data-lang-switch]').forEach(function(button) {
    if (button.__themeLangSwitchBound) return;
    button.__themeLangSwitchBound = true;

    button.addEventListener('click', function(e) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      e.stopPropagation();
      switchLanguage(button.getAttribute('data-lang-switch'));
    });
  });
}

function closeDeliveryLocationPopup() {
  if (typeof window.closeDeliveryLocationPopup === 'function') {
    window.closeDeliveryLocationPopup();
  }
}

function initCountrySelector() {
  if (countrySelectorInitialized) return;
  const countryButtons = document.querySelectorAll('[data-popup-trigger="delivery-location"]');

  if (!countryButtons.length) return;
  countrySelectorInitialized = true;

  countryButtons.forEach(function(countryButton) {
    if (countryButton.classList.contains('lang-switch') && countryButton.hasAttribute('data-lang-switch')) {
      return;
    }

    countryButton.addEventListener('click', function(e) {
      if (countryButton.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof window.closeMobileMenuDrawer === 'function') {
        window.closeMobileMenuDrawer();
      }
      openDeliveryLocationPopup();
    });
  });

  document.querySelectorAll('[data-country-selector]').forEach(function(wrapper) {
    wrapper.addEventListener('click', function(e) {
      if (e.target.closest('a, button')) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof window.closeMobileMenuDrawer === 'function') {
        window.closeMobileMenuDrawer();
      }
      openDeliveryLocationPopup();
    });
  });

  const confirmButton = document.getElementById('delivery-location-confirm-region');
  const availableCodes = getAvailableShopifyCountryCodes();
  const countrySelect = document.getElementById('delivery-location-country');
  if (countrySelect && availableCodes.length) {
    Array.from(countrySelect.options).forEach(function(option) {
      option.disabled = !availableCodes.includes(normalizeCountryCode(option.value));
    });
  }

  if (confirmButton) {
    confirmButton.addEventListener('click', function(e) {
      const countrySelect = document.getElementById('delivery-location-country');
      const languageSelect = document.getElementById('delivery-location-language');
      if (!countrySelect || !countrySelect.value || !languageSelect) return;

      e.preventDefault();
      e.stopPropagation();

      if (langSwitchInFlight || marketNavigationInFlight) return;

      const selectedCountry = normalizeCountryCode(countrySelect.value);
      const selectedLanguage = normalizeCountryCode(languageSelect.value) === 'AR' ? 'ar' : 'en';
      const current = getCurrentContext();
      const target = getTargetForCountry(selectedCountry);
      const nextLanguage = supportsArabicLanguage(selectedCountry) ? selectedLanguage : 'en';

      saveCurrentMarketContext({
        ...current,
        ...target,
        countryCode: selectedCountry,
        country: selectedCountry.toLowerCase(),
        locale: nextLanguage,
        explicit: true,
        locationSource: 'manual',
        source: 'country_confirm'
      });
      if (typeof window.closeDeliveryLocationPopup === 'function') {
        window.closeDeliveryLocationPopup();
      }

      if (selectedCountry === getShopifyCountryCode() && nextLanguage === getCurrentPageLocale()) {
        window.theme.shopifyCountry = selectedCountry;
        window.theme.country = selectedCountry;
        window.theme.currency = target.currency;
        document.dispatchEvent(new CustomEvent('theme:convert-prices'));
        syncCountryDisplay();
        return;
      }

      langSwitchInFlight = true;
      setMarketSwitchBusy(true);
      if (!submitShopifyLocalization(selectedCountry, nextLanguage)) {
        langSwitchInFlight = false;
        setMarketSwitchBusy(false);
        openDeliveryLocationPopup();
      }
    });
  }

}

function showComingSoonToast(feature) {
  if (typeof window.showSystemToast === 'function') {
    window.showSystemToast({
      title: `${feature} - Coming soon`,
      duration: 3000,
    });
    return;
  }
}

function closeUserDropdowns(exceptWrapper) {
  document.querySelectorAll(USER_WRAPPER_SELECTOR).forEach(function(wrapper) {
    if (exceptWrapper && wrapper === exceptWrapper) return;
    wrapper.removeAttribute('data-dropdown-open');
    const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
    if (button) button.setAttribute('aria-expanded', 'false');
  });
}

function toggleUserDropdown(wrapper) {
  if (!wrapper) return;
  const isOpen = wrapper.getAttribute('data-dropdown-open') === 'true';
  if (isOpen) {
    closeUserDropdowns();
    return;
  }

  closeUserDropdowns(wrapper);
  wrapper.setAttribute('data-dropdown-open', 'true');
  const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
  if (button) button.setAttribute('aria-expanded', 'true');
}

function initUserDropdowns() {
  document.querySelectorAll(USER_WRAPPER_SELECTOR).forEach(function(wrapper) {
    const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
    const dropdown = wrapper.querySelector(USER_DROPDOWN_SELECTOR);
    if (!button || !dropdown) return;
    button.setAttribute('aria-haspopup', 'menu');
    button.setAttribute('aria-expanded', wrapper.getAttribute('data-dropdown-open') === 'true' ? 'true' : 'false');
  });

  if (userDropdownEventsBound) return;

  document.addEventListener('click', function(e) {
    const button = e.target.closest(USER_BUTTON_SELECTOR);
    if (button) {
      e.preventDefault();
      e.stopPropagation();
      toggleUserDropdown(button.closest(USER_WRAPPER_SELECTOR));
      return;
    }

    if (!e.target.closest(USER_WRAPPER_SELECTOR)) {
      closeUserDropdowns();
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') closeUserDropdowns();
  });

  userDropdownEventsBound = true;
}

document.addEventListener('click', function(e) {
  const burger = e.target.closest('.btn_menu_burger');
  if (burger) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const drawer = document.getElementById('mobile-menu-drawer');
    if (drawer) {
      if (drawer.parentNode !== document.body) {
        document.body.appendChild(drawer);
      }

      if (typeof window.openMobileMenuDrawer === 'function') {
        window.openMobileMenuDrawer();
      } else {
        const scrollY = window.__themeDocumentScrollY ? window.__themeDocumentScrollY() : (window.pageYOffset || 0);
        document.body.style.top = '-' + scrollY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.classList.add('menu-drawer-open');
        drawer.classList.remove('is-hidden');
        void drawer.offsetWidth;
        drawer.classList.add('is-open');
      }
    }

    return;
  }

  const comingSoonTrigger = e.target.closest('[data-coming-soon]');
  if (!comingSoonTrigger) return;

  e.preventDefault();
  showComingSoonToast(comingSoonTrigger.getAttribute('data-coming-soon'));
}, true);

stripPreviewBuilderParam();
initUserDropdowns();

function boot() {
  if (isShopifyDesignMode()) {
    initCountrySelector();
    initLangSwitch();
    syncCountryDisplay();
    return;
  }

  syncStoredMarketWithCurrentUrl();
  initCountrySelector();
  initLangSwitch();
  syncCountryDisplay();
  runRegionQuestionFlow();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', function() {
  initUserDropdowns();
  initLangSwitch();
  syncCountryDisplay();
});
